<template>
  <modal @close_modal="close_modal"
         :title="modalTitle"
         :loading="loading"
  >
    <template v-slot:tabs>
      <v-tabs>
        <v-tab @click="tab_selected='tab_model_type'">{{$t('set_train_c.edit_model_type')}}</v-tab>
        <v-tab @click="tab_selected='tab_label_sets'">{{$t('set_train_c.edit_labelsets')}}</v-tab>
        <v-tab @click="tab_selected='tab_advanced'">{{$t('set_train_c.edit_advanced')}}</v-tab>
      </v-tabs>
    </template>
    <template v-slot:content>
      <div v-if="related_models.length > 0">
        <v-alert type="warning" outlined dense>
          {{$t('models_and_train_configs.readonly_because_related_models')}}
        </v-alert>
      </div>

      <div v-if="tab_selected === 'tab_model_type'">
        <b>{{$t('models_and_train_configs.choose_name_and_descriptions')}}</b><br><br>
        <row>
          <col6>
            <v-text-field
                    v-model="name"
                    required
                    dense
                    outlined
            ></v-text-field>
          </col6>
          <col6>
            <v-textarea
              v-model="description"
              dense
              outlined
              rows="1"
            ></v-textarea>
          </col6>
        </row>
        <b>{{$t('models_and_train_configs.select_model_type')}}</b><br><br>
        <friendly-model-selector
            @clicked="select_model"
            @apply-options="apply_friendly_options"
            :selected_model_type="selected_model_type"
            :target_device="selectedTargetDevice"
            :model_buttons="all_available_model_buttons"
            :disabled="has_related_models"></friendly-model-selector>
        <p v-if="selected_model_id !== false">
          <b>{{$t('models_and_train_configs.set_your_parameters')}}</b><br><br>
          <parameters
            @select="select"
            :all_parameters="available_model_parameter_for_selected_model"
            :selected_options="selected_options_for_selected_model_id"
            :disabled="has_related_models">
          </parameters>
          <show-alert v-if="selected_model_id!==false && selected_label_set_id==null" borderPosition="right" :alert-message="$t('models_and_train_configs.no_labelset_selected')"></show-alert>
        </p>
      </div>
      <div v-if="tab_selected === 'tab_label_sets'">
        <manage-label-sets
        :show_select_boxes="!has_related_models"
        @selected_label_set_id='selectedLabelSetId'
        :selected_label_set_id="selected_label_set_id"
        ></manage-label-sets>
      </div>

      <div v-if="tab_selected === 'tab_advanced'">
        <row>
          <col3>Only annotations contained in:</col3>
          <col3>
            <search-label-autocomplete
              :value="cropOnLabelDisplay"
              :label_type="1"
              :disabled="has_related_models"
              :clear_on_select="false"
              :track_last_used="true"
              :placeholder="$t('filter_tree.label_search_placeholder')"
              @label-selected="select_crop_on_label"
              @cleared="select_crop_on_label(null)"
            />
          </col3>
        </row>
        <label-multi-select
          :value="selected_required_labels"
          :disabled="has_related_models"
          @change="onRequiredLabelsChange"
        ></label-multi-select>
        <row12>
          <parameters
              @select="select"
              :all_parameters="available_model_parameter_for_selected_model"
              :selected_options="selected_options_for_selected_model_id"
              :advanced="true"
              v-if="selected_model_id !== false"
              :disabled="has_related_models"
              ></parameters>
        </row12>
      </div>


    </template>


    <template v-slot:buttons>
      <v-btn
          color="primary"
          text
          id="cancel_button"
          @click="cancel"
      >
        {{$t('set_train_c.edit_cancel')}}
      </v-btn>

      <v-btn
          :disabled="!selected_model_id || !selected_label_set_id"
          color="primary"
          text
          id="save_button"
          @click="save"
      >
        {{$t('set_train_c.edit_save')}}
        <v-icon
            dark
            primary
            right
        >
          fas fa-level-down-alt fa-rotate-90
        </v-icon>
      </v-btn>
    </template>

  </modal>
</template>

<script>
import Modal from "@/components/Modal";
import Col6 from "@/components/layout/Col6";
import Row from "@/components/layout/Row";
import Parameters from "@/components/train_configs/Parameters";
import FriendlyModelSelector from "@/components/train_configs/FriendlyModelSelector.vue";
import Row12 from "@/components/layout/Row12.vue";
import Col3 from "@/components/layout/Col3";
import LabelMultiSelect from "@/components/train_configs/LabelMultiSelect";
import SearchLabelAutocomplete from "@/components/SearchLabelAutocomplete.vue";
import LoadAvailableModelsMixin from "@/mixins/LoadAvailableModelsMixin.vue";
import ManageLabelSets from "@/components/ManageLabelSets.vue";
import ShowAlert from "@/components/ShowAlert.vue";
import { BOXIFY_TARGET_PRESETS } from "@/utils/boxifyTargetPresets";

export default {
  name: "EditTrainConfiguration",
  components: {Row12, FriendlyModelSelector, Parameters, Row, Col6, Col3, Modal, ManageLabelSets, ShowAlert, LabelMultiSelect, SearchLabelAutocomplete},
  mixins: [LoadAvailableModelsMixin],
  props: {
    train_config: {required: true},
    models: Array,
    is_new_train_conf: {type: Boolean, default: false},
    is_copy: {type: Boolean, default: false},
  },
  data() {
    return {
      selected_options: {},
      selected_model_id: false,
      selected_model_type: false,
      show_advanced_options: true,
      name: '',
      description: '',
      tab_selected: 'tab_model_type',
      loading: true,
      label_ids: [],
      model_type: '',
      image_size: 0,
      crop_on_label: false,
      selected_label_ids: [],
      selected_label_set_id : null,
      selected_required_labels: '',
    }
  },
  watch: {
    train_config: {
      deep: true,
      handler() {
        if ((this.is_new_train_conf && !this.is_copy) || !this.train_config?.id) {
          return;
        }
        if (!this.available_models_loaded) {
          return;
        }
        this.load_current_config();
      },
    },
    available_models_loaded() {
      this.load_current_config();
    },
  },
  mounted() {
    this.name = this.train_config.name || '';
    this.description = this.train_config.description || '';
    if (this.is_new_train_conf && !this.is_copy) {
      this.loading = false;
      return;
    }
    if (this.available_models_loaded) {
      this.load_current_config();
    }
  },
  methods: {
    select_model(selected_model_button) {
      this.on_click(selected_model_button.id);
    },
    /** Options from the guided picker (e.g. Use ArcFace Yes/No). Applied after model defaults. */
    apply_friendly_options(options) {
      if (!options || !this.selected_model_id) {
        return;
      }
      this.$nextTick(() => {
        for (const [parameter_id, value] of Object.entries(options)) {
          this.select({ parameter_id, value });
        }
      });
    },
    select_label_ids(label_ids) {
      this.selected_label_ids = label_ids;
    },
    select(value) {
      console.log('[EditTrainConfiguration] select() parameter_id:', value.parameter_id, 'value:', value.value, 'model:', this.selected_model_id);
      let selected_options = this.selected_options;
      if (!selected_options[this.selected_model_id]) {
        this.selected_options[this.selected_model_id] = {};
      }
      this.selected_options[this.selected_model_id][value.parameter_id] = value.value;
      if (value.parameter_id === 'target_device' && this.selected_model_id === 'boxify') {
        const preset = BOXIFY_TARGET_PRESETS[value.value];
        if (preset) {
          for (const [parameter_id, presetValue] of Object.entries(preset)) {
            this.selected_options[this.selected_model_id][parameter_id] = String(presetValue);
          }
        }
      }
    },
    select_crop_on_label(value) {
      if (!value) {
        this.crop_on_label = false;
        return;
      }
      this.crop_on_label = typeof value === 'string' ? value : (value.name || false);
    },
    onRequiredLabelsChange(value) {
      this.selected_required_labels = value;
    },
    on_click(model_type) {
      this.selected_model_type = model_type;
      this.selected_model_id = model_type;

      if(!Object.keys(this.selected_options).includes(this.selected_model_id)) {
        this.selected_options[this.selected_model_id] = {};
      }

      if(!this.available_model_parameter_for_selected_model) {
        return;
      }

      for(let i=0; i<this.available_model_parameter_for_selected_model.length; i++) {
        let param = this.available_model_parameter_for_selected_model[i]

        if(param.default_value && !Object.keys(this.selected_options[this.selected_model_id]).includes(param.id)) {
          this.selected_options[this.selected_model_id][param.id] = param.default_value.toString();
        }
      }
    },
    async save() {
      let model = this.available_models.filter(model => model.id === this.selected_model_id)[0];
      let type = model.id;
      const modelKey = this.selected_model_type;
      if (!modelKey || !this.selected_options[modelKey]) {
        alert('Could not save: model options are not loaded');
        return;
      }
      let meta = this.selected_options[modelKey];
      console.log('[EditTrainConfiguration] save() meta before submit:', JSON.stringify(meta));
      meta['selected_model_id'] = this.selected_model_id
      meta['type'] = type;
      meta['crop_on_label'] = this.crop_on_label
      meta['required_labels'] = this.selected_required_labels
      if (this.selected_label_set_id === null) {
        alert('Could not save because there is no label set');
        return;
      }
      meta['label_set_id'] = this.selected_label_set_id

      meta['model_type'] = model.model_type;
      let train_config = {
        id: this.is_copy ? undefined : this.train_config.id,
        name: this.name,
        repeat_after_days: null,
        description: this.description,
        meta: JSON.stringify(meta)
      }

      if(train_config.id) {
          await this.$store.dispatch('train_config/patch', { item: train_config, dispatch: this.$store.dispatch });
      }
      else {
          await this.$store.dispatch('train_config/add', train_config);
      }

      this.$emit('reload')
      this.$emit('close_modal')
    },
    cancel () {
      this.$emit('close_modal')
    },
    toggle_label(label_id) {
      let label_ids = this.label_ids
      if(label_ids.includes(label_id)) {
        label_ids = label_ids.filter((id)=> {return id !== label_id});
      }
      else {
        label_ids.push(label_id)
      }
      this.label_ids = label_ids
    },
    close_modal() {
      this.$emit('close_modal')
    },
    async load_current_config() {
      if (this.is_new_train_conf && !this.is_copy) {
        this.loading = false;
        return;
      }
      const rawMeta = this.train_config.meta;
      if (rawMeta == null || rawMeta === '') {
        this.loading = false;
        return;
      }
      let meta;
      try {
        meta = typeof rawMeta === 'string' ? JSON.parse(rawMeta) : rawMeta;
      } catch (e) {
        console.warn('Invalid train_config.meta', e);
        this.loading = false;
        return;
      }
      if (!meta || typeof meta !== 'object') {
        this.loading = false;
        return;
      }
      console.log('[EditTrainConfiguration] load_current_config parsed meta:', meta);

      let crop_on_label = meta.crop_on_label
      this.crop_on_label = crop_on_label

      let selected_label_set_id = meta.label_set_id
      this.selected_label_set_id = selected_label_set_id

      // Load required_labels from meta (comma-separated string of label names)
      if (meta.required_labels != null && meta.required_labels !== '') {
        this.selected_required_labels = String(meta.required_labels);
      }

      let selected_model_id = meta.type
      if (selected_model_id === 'ocr_rec' || selected_model_id === 'ocr_det') {
        selected_model_id = 'paddleocr'
      }

      if(selected_model_id) {
        this.on_click(selected_model_id)
      }

      if(!meta.selected_model_id) {
        // OLD STYLE configs: nothing more to map into selected_options
        this.loading = false;
        return;
      }

      const bucketKey = this.selected_model_id;
      if (!this.selected_options[bucketKey]) {
        this.selected_options[bucketKey] = {};
      }

      let parameter_keys = Object.keys(meta);

      for(let i=0; i<parameter_keys.length; i++) {
        let parameter_key = parameter_keys[i]
        if(parameter_key === 'selected_model_id' || parameter_key === 'required_labels') {
          continue;
        }
        let v = meta[parameter_key];
        if (parameter_key === 'training_set_filters' || parameter_key === 'validation_set_filters') {
          v = this.normalizeFiltersMetaValue(v);
        }
        this.selected_options[bucketKey][parameter_key] = v;
      }

      console.log('[EditTrainConfiguration] load_current_config finished, selected_options[' + bucketKey + ']:', JSON.stringify(this.selected_options[bucketKey]));
      this.loading = false;
    },
    /** Ensure filter fields are a single JSON string of filter groups (handles double-encoded or parsed arrays). */
    normalizeFiltersMetaValue(value) {
      if (value == null || value === '') {
        return '';
      }
      if (Array.isArray(value)) {
        return JSON.stringify(value);
      }
      if (typeof value === 'string') {
        const t = value.trim();
        if (!t) {
          return '';
        }
        try {
          let parsed = JSON.parse(t);
          if (typeof parsed === 'string') {
            try {
              parsed = JSON.parse(parsed);
            } catch (e) {
              return t;
            }
          }
          if (Array.isArray(parsed)) {
            return JSON.stringify(parsed);
          }
        } catch (e) {
          return t;
        }
        return t;
      }
      return String(value);
    },
    selectedLabelSetId(selected_label_set_id) {
      if (this.selected_label_set_id !== selected_label_set_id) {
        this.selected_required_labels = '';
      }
      this.selected_label_set_id = selected_label_set_id;
    },
  },
  computed: {
    modalTitle() {
      if (this.is_copy) {
        return this.$t('set_train_c.copy_title');
      }
      return this.is_new_train_conf ? this.$t('set_train_c.add_title') : this.$t('set_train_c.edit_title');
    },
    has_related_models() {
      return this.related_models.length > 0;
    },
    cropOnLabelDisplay() {
      return this.crop_on_label && this.crop_on_label !== false ? this.crop_on_label : '';
    },
    related_models() {
      if(!this.models) {
        return [];
      }
      return this.models.filter(model => model.train_config_id === this.train_config.id);
    },
    available_model_parameter_for_selected_model() {
      let parameters = [...this.parameters_for_all];

      if(this.available_models.filter(model => model.id === this.selected_model_type).length == 0) {
        return;
      }

      let selected_model = this.available_models.filter(model => model.id === this.selected_model_type)[0];

      const applyOverrides = (overrides) => {
        if (!overrides || !overrides.length) {
          return;
        }
        const byId = new Map(parameters.map((p) => [p.id, p]));
        for (const p of overrides) {
          byId.set(p.id, p);
        }
        parameters = Array.from(byId.values());
      };

      applyOverrides(this.parameters_per_model_type[selected_model.model_type]);
      applyOverrides(this.parameters_per_available_model[selected_model.id]);
      return parameters;
    },
    all_available_model_buttons() {
      let buttons = []
      for(let i=0; i<this.available_models.length; i++) {
        let model_type = this.available_models[i].type
        let model_button = this.available_models[i];
        model_button['model_type'] = model_type
        buttons.push(model_button)
      }
      return buttons
    },
    selected_options_for_selected_model_id() {
      if (!this.selected_model_type) {
        return undefined;
      }
      return this.selected_options[this.selected_model_type];
    },
    selectedTargetDevice() {
      const options = this.selected_options_for_selected_model_id;
      return options && options.target_device ? options.target_device : '';
    },
  },
}
</script>

<style scoped>

</style>
